import { useState, useCallback } from "react";
import { Client, settings } from "../library/allauth";
import { getCSRFToken } from "../library/django";

export const useVideoPlaylist = (selectedRegion) => {
  const [playlist, setPlaylist] = useState([]);
  const [playlistLoading, setPlaylistLoading] = useState(false);
  const [playlistError, setPlaylistError] = useState(null);
  const [allFilesData, setAllFilesData] = useState([]);

  const getFetchOptions = useCallback(() => {
    let baseUrl = "";
    try {
      baseUrl = settings.baseUrl;
    } catch { }

    const options = { headers: {} };

    if (settings.client === Client.BROWSER) {
      const csrfToken = getCSRFToken();
      if (csrfToken) {
        options.headers["X-CSRFToken"] = csrfToken;
      }
    }
    if (settings.withCredentials) {
      options.credentials = "include";
    }

    return { baseUrl, options };
  }, []);

  const fetchPlaylist = useCallback(async (filters) => {
    if (!filters || !selectedRegion) return;

    setPlaylistLoading(true);
    setPlaylistError(null);

    try {
      const regionId = selectedRegion.region_id || selectedRegion;
      const formattedDate = filters.date.replace(/-/g, "/");
      const { baseUrl, options } = getFetchOptions();

      const apiUrl = `${baseUrl}/file/get_path_list/?region_id=${regionId}&system_id=${filters.systemId}&component_id=${filters.componentId}&date=${formattedDate}&hour=${filters.hour}`;

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
        credentials: options.credentials,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const allFiles = data.files || [];

      const videoFiles = allFiles.filter((file) => file.filename.endsWith(".mp4"));

      const transformedFiles = videoFiles.map((file, index) => {
        const fileNameParts = file.filename.replace(".mp4", "").split("_");
        const hour = fileNameParts[3] || "00";
        const minute = fileNameParts[4] || "00";
        const second = fileNameParts[5] || "00";
        
        const thumbnailFile = allFiles.find(
          (f) => f.file_path === file.file_path.replace(".mp4", ".png")
        );

        return {
          id: index + 1,
          title: `${hour}:${minute}:${second}`,
          duration: "05:00",
          description: file.filename,
          videoToken: file.token,
          thumbnailToken: thumbnailFile?.token || null,
          filename: file.filename,
          size: file.size,
          timestamp: `${hour}:${minute}:${second}`,
          path: file.file_path,
          thumbnailpath: thumbnailFile?.file_path || null,
        };
      });

      if (transformedFiles.length === 0) {
        setPlaylistError("No video files found for the selected filters");
      }

      setAllFilesData(transformedFiles);
      
      const transformedPlaylist = transformedFiles.filter((file) => {
        return file.path.includes("/Module_1/");
      });
      
      setPlaylist(transformedPlaylist);

      return transformedPlaylist;
    } catch (error) {
      console.error("Failed to fetch playlist:", error);
      setPlaylistError(error.message);
      setPlaylist([]);
      return [];
    } finally {
      setPlaylistLoading(false);
    }
  }, [selectedRegion, getFetchOptions]);

  const getFileUrl = useCallback(async (token) => {
    try {
      const { baseUrl, options } = getFetchOptions();

      const response = await fetch(`${baseUrl}/file/get_file/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
        credentials: options.credentials,
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status}`);
      }

      const blob = await response.blob();
      return window.URL.createObjectURL(blob);
    } catch (error) {
      console.error("Error fetching file with token:", error);
      return null;
    }
  }, [getFetchOptions]);

  return {
    playlist,
    playlistLoading,
    playlistError,
    allFilesData,
    fetchPlaylist,
    getFileUrl,
  };
};

